import React, { useState } from "react";
import { useIntl } from "react-intl";
import styles from "./Sidebar.module.scss";

export interface SidebarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: () => void;
}

export interface SidebarProps {
  items: SidebarItem[];
  activeId?: string;
  onItemClick?: (id: string) => void;
  className?: string;
  photoUrl?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  activeId,
  onItemClick,
  className = "",
  photoUrl
}) => {
  const intl = useIntl();
  const [active, setActive] = useState<string>(activeId ?? items[0]?.id ?? "");
  const [loaded, setLoaded] = useState(false);

  const handleClick = (item: SidebarItem) => {
    setActive(item.id);
    item.onClick?.();
    onItemClick?.(item.id);
  };

  return (
    <nav
      className={`${styles.sidebar} ${className}`}
      aria-label={intl.formatMessage({ id: "sidebar.profileNavigation", defaultMessage: "Profile navigation" })}
    >
      <div className={styles.wrapper}>
        {!loaded && <div className={styles.skeleton} />}

        {photoUrl && (
          <img
            src={photoUrl || "/photos/no-photo.jpg"}
            alt="Profile"
            className={styles.photo}
            onLoad={() => setLoaded(true)}
            style={{ opacity: loaded ? 1 : 0 }}
          />)}
      </div>

      <ul className={styles.list}>
        {items.map((item) => {
          const isActive = active === item.id;
          return (
            <li key={item.id} className={styles.item}>
              <a
                href={item.href ?? "#"}
                className={`${styles.link} ${isActive ? styles.linkActive : ""}`}
                onClick={(e) => {
                  if (!item.href || item.href === "#") e.preventDefault();
                  handleClick(item);
                }}
                aria-current={isActive ? "page" : undefined}
              >
                <span className={styles.icon} aria-hidden="true">
                  {item.icon}
                </span>
                <span className={styles.label}>{item.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default Sidebar;
